
import React from "react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { DateTime } from "luxon";
import FieldWrapper from "../fields/FieldWrapper";
import { Control, Path, PathValue, UseFormSetValue, useWatch } from "react-hook-form";
import { FormFieldType } from "@/types/form";

interface DateFieldProps<TFieldValues extends Record<string, any>> {
  control: Control<TFieldValues>;
  setValue: UseFormSetValue<TFieldValues>;
  name: Path<TFieldValues>;
  field: FormFieldType;
  isRequired?: boolean;
}

export const DateField = <TFieldValues extends Record<string, any>>(
  props: DateFieldProps<TFieldValues>
) => {
  const fieldValue = useWatch({
    control: props.control,
    name: props.name
  }) as string | undefined;

  const handleChange = (date: Date | undefined) => {
    if (date) {
      const datetime = DateTime.fromJSDate(date).setZone("Asia/Bangkok", { keepLocalTime: true });
      props.setValue(props.name, datetime.toISO() as PathValue<TFieldValues, Path<TFieldValues>>);
    } else {
      props.setValue(props.name, undefined as unknown as PathValue<TFieldValues, Path<TFieldValues>>);
    }
  };

  return (
    <FieldWrapper element={props.field}>
      <Popover>
        <PopoverTrigger asChild >
          <Button
            variant="outline"
            className={cn(
              "w-full justify-start text-left font-normal",
              !fieldValue && "text-muted-foreground"
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {fieldValue ?
              format(new Date(fieldValue), "PPP") :
              "Pick a date"
            }
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0">
          <Calendar
            mode="single"
            selected={fieldValue ? new Date(fieldValue) : undefined}
            onSelect={handleChange}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    </FieldWrapper>
  );
};

export default DateField;
